import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { NotificationService } from '../../../../core/_infrastructure/classes/services/notification.service';
import { FilterSearchingBaseViewModel } from '../../../../core/_infrastructure/types/common';
import {
    EmpresaDeshidratadoraItemResultsViewModel,
    EmpresaDeshidratadoraItemViewModel,
    FilterSearchingEmpresaDeshidratadoraViewModel,
} from '../empresadeshidratadora.types';
import { DialogMode } from 'src/app/components/_campanias/types/enum';
import { ListBase } from 'src/app/components/core/_infrastructure/classes/listBase';
import { proxiedPropertiesOf } from 'src/app/components/core/_infrastructure/types/utils';
import { AutorizarRecintoPIAEDCamViewModel } from 'src/app/components/_campanias/types/common-cam-types';

@Component({
    selector: 'app-empresadeshidratadora-list',
    templateUrl: './empresadeshidratadora-list.component.html',
    styleUrls: ['./empresadeshidratadora-list.component.scss'],
})
export class EmpresaDeshidratadoraListComponent extends ListBase implements OnInit, OnChanges {
    @Input() result: EmpresaDeshidratadoraItemResultsViewModel;
    @Input() lastFilter: FilterSearchingBaseViewModel;
    @Input() mode: DialogMode;
    @Input() readonly: boolean;

    @Output() search = new EventEmitter<FilterSearchingBaseViewModel>();
    @Output() edit = new EventEmitter<number | null>();
    @Output() view = new EventEmitter<number | null>();
    @Output() delete = new EventEmitter<number | null>();
    @Output() undoDelete = new EventEmitter<number | null>();
    @Output() authorize = new EventEmitter<AutorizarRecintoPIAEDCamViewModel>();
    @Output() selectedEmpresas = new EventEmitter<any>();

    @ViewChild('tableSearch') turboTable: Table;

    cols: any[];

    arraySelectedEmpresas: EmpresaDeshidratadoraItemViewModel[] = [];
    dialogMode = DialogMode;

    constructor(protected notificacionService: NotificationService) {
        super(notificacionService);
    }

    ngOnInit(): void {
        this.initCols();
        this.loading = false;
    }

    private initCols(): void {
        const empresaVM = proxiedPropertiesOf<EmpresaDeshidratadoraItemViewModel>();
        this.cols = [
            { field: 'ExpandLink', header: '', reduce: true },
            { field: 'Link', header: '', reduce: true },
            { field: empresaVM.Id?.toString(), header: 'Id', display: 'none' },
            { field: empresaVM.NumeroRegistro?.toString(), header: 'Número de Registro' },
            { field: empresaVM.NombreIndustria?.toString(), header: 'Nombre de Empresa' },
            { field: empresaVM.IdProvinciaDescripcion?.toString(), header: 'Provincia' },
            { field: empresaVM.IdMunicipioDescripcion?.toString(), header: 'Municipio' },
            { field: empresaVM.Activo?.toString(), header: 'Activo' },
            { field: empresaVM.DadoDeBaja?.toString(), header: 'Baja en U.I.' },
        ];

        this.initSelection();
    }

    /**
     * Este componente es utilizado desde un p-dialog es un contenedor que se oculta/muestra a demanda,
     * por eso hay que borrar los datos obtenidos, ya que perduran.
     * Para que la siguiente vez al entrar no salgan marcados los checkbox en la tabla
     */
    private initSelection(): void {
        this.arraySelectedEmpresas.splice(0, this.arraySelectedEmpresas.length);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.result?.currentValue) {
            this.result = { ...changes.result.currentValue };
            this.loading = false;
            setTimeout(() => {
                if (this.turboTable) {
                    this.turboTable.sortOrder = this.lastFilter?.Asc;
                    this.turboTable.sortField = this.lastFilter?.OrderBy;
                    this.turboTable.first = this.lastFilter.PageNumber * this.result.PageSize;
                }
            }, 100);
        }
    }

    onSearchLazy(event: any): void {
        if (!event.rows || event.sortField === 'Link' || event.sortField === 'DadoDeBaja') {
            return;
        }
        let pageNumber = event.first / event.rows;
        this.loading = true;

        const f = {
            PageNumber: pageNumber,
            Asc: event.sortOrder,
            OrderBy: event.sortField,
        } as FilterSearchingEmpresaDeshidratadoraViewModel;

        this.search.emit(f);
    }

    onEdit(item: EmpresaDeshidratadoraItemViewModel): void {
        this.edit.emit(item?.Id);
    }

    onView(item: EmpresaDeshidratadoraItemViewModel): void {
        this.view.emit(item?.Id);
    }

    onDelete(item: EmpresaDeshidratadoraItemViewModel): void {
        this.delete.emit(item?.Id);
    }

    onUndoDelete(item: EmpresaDeshidratadoraItemViewModel): void {
        this.undoDelete.emit(item?.Id);
    }

    onAuthorize(item: EmpresaDeshidratadoraItemViewModel, IdCampania: number): void {
        const payload = new AutorizarRecintoPIAEDCamViewModel();
        payload.Id = item.Id!;
        payload.IdCampania = IdCampania;
        this.authorize.emit(payload);
    }

    /**
     * This event is used only when in dialogMode.
     * It will have to tell which kind of selection the user has made.
     * The data selected will be raised alongside the type of selection.
     * @param $event
     * @param rowData EmpresaDeshidratadoraItemViewModel
     */
    onSelect($event: any, rowData: any): void {
        $event.stopPropagation();
        if (this.mode == DialogMode.Dialog) {
            let arraySelected = this.arraySelectedEmpresas.slice();
            this.selectedEmpresas.emit({ selection: arraySelected, dblclick: false });
        }
    }

    showEditBtn(rowData: any): boolean {
        return this.mode === this.dialogMode.Basic && !this.esDesautorizada(rowData);
    }

    showViewBtn(rowData: any): boolean {
        return this.mode === this.dialogMode.Embedded || this.esDesautorizada(rowData);
    }

    showDeleteBtn(rowData: any): boolean {
        return !this.readonly && this.mode !== this.dialogMode.Dialog && (!rowData?.MarkToDelete || rowData?.MarkToDelete === undefined);
    }

    showUndeleteBtn(rowData: any): boolean {
        return this.mode === this.dialogMode.Embedded && rowData?.MarkToDelete;
    }

    showAuthorizeBtn(rowData: any): boolean {
        return this.esDesautorizada(rowData) && this.mode === this.dialogMode.Basic;
    }

    /**
     * Una empresa estará en lista negra si está desautorizada en alguna campaña.
     * @param rowData EmpresaDeshidratadoraItemViewModel
     * @returns boolean
     */
    esDesautorizada(rowData: EmpresaDeshidratadoraItemViewModel): boolean {
        return rowData?.CampaniasDesautorizado?.length > 0;
    }
}
